import React, { useMemo } from "react";
import ReactSelect from "react-select";

// Custom components
import ValueContainer from "./ValueContainer";
import Menu from "./Menu";
import MenuList from "./MenuList";
import Option from "./Option";
import TransparentColor from "./ColorCircle/colors/transparent.png";

import { SketchPicker } from "react-color";

const ColorSelect = ({ availableColors = ["*"], ...props }) => {
  const {
    allowNoColors,
    allowTransparent = false,
    disableAllColors = false,
    deprecateTransparency = false,
  } = props;

  const themeFct = (theme) => {
    return {
      ...theme,
      borderRadius: 4,
      spacing: {
        ...theme.spacing,
        menuGutter: 0,
      },
      colors: {
        danger: "#DE350B",
        dangerLight: "#FFBDAD",
        neutral0: "hsl(0, 0%, 100%)",
        neutral5: "hsl(0, 0%, 95%)",
        neutral10: "hsl(0, 0%, 90%)",
        neutral20: "hsl(0, 0%, 80%)",
        neutral30: "hsl(0, 0%, 70%)",
        neutral40: "hsl(0, 0%, 60%)",
        neutral50: "hsl(0, 0%, 50%)",
        neutral60: "hsl(0, 0%, 40%)",
        neutral70: "hsl(0, 0%, 30%)",
        neutral80: "hsl(0, 0%, 20%)",
        neutral90: "hsl(0, 0%, 10%)",
        primary: "#EEE",
        primary25: "#EEE",
        primary50: "#EEE",
        primary75: "#EEE",
      },
    };
  };

  const isSketchPicker = useMemo(
    () => availableColors.some((color) => color === "*"),
    [availableColors]
  );

  const options = useMemo(() => {
    let result = availableColors.map((ac) => {
      if (ac.textureUrl) {
        return {
          value: ac.name,
          label: ac.label,
          png: ac.textureUrl,
          isDisabled: disableAllColors,
          isDeprecated: ac.disabled || ac.hidden || false,
        };
      } else {
        return {
          value: ac.name,
          label: ac.label,
          hexCode: ac.hex,
          isDisabled: disableAllColors,
          isDeprecated: ac.disabled || ac.hidden || false,
        };
      }
    });

    if (allowNoColors) {
      result = [
        ...result,
        {
          value: "inherited",
          label: "no_color",
          isDisabled: disableAllColors,
        },
      ];
    }

    if (allowTransparent) {
      result = [
        ...result,
        {
          value: "transparent",
          label: "transparent",
          isDisabled: disableAllColors,
          png: TransparentColor,
          isDeprecated: deprecateTransparency || false,
        },
      ];
    }

    return result;
  }, [
    availableColors,
    allowNoColors,
    allowTransparent,
    disableAllColors,
    deprecateTransparency,
  ]);

  if (isSketchPicker) {
    return (
      <SketchPicker
        color={props.value.value}
        onChange={(e) => {
          props.onChange({ value: e.hex });
        }}
      />
    );
  }

  const styles = {
    control: (css, { isDisabled }) => ({
      ...css,
      backgroundColor: disableAllColors ? "#F9FAFB" : "transparent", //can't use Disabled because it won't show the options if isDisabled=true for the control
      cursor: isDisabled ? "not-allowed" : "pointer",
    }),
    option: (css, { isDisabled }) => ({
      ...css,
      cursor: isDisabled ? "not-allowed" : "pointer",
    }),
    clearIndicator: (css, { isDisabled }) => ({
      ...css,
      cursor: isDisabled ? "not-allowed" : "pointer",
    }),
  };

  return (
    <ReactSelect
      {...props}
      components={{
        ValueContainer,
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
        Menu,
        MenuList,
        Option,
      }}
      options={options}
      theme={themeFct}
      styles={styles}
      isSearchable={false}
    />
  );
};

export default ColorSelect;
